import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  MoreHorizontal,
  Edit,
  Trash,
  Calendar,
  User,
  Car,
  Fuel,
  FileText,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { VehicleListItem } from '@/app/types/vehicle';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';
import React from 'react';

// Custom events for row actions
export const editVehicleEvent = 'edit-vehicle';
export const deleteVehicleEvent = 'delete-vehicle';
export const createJobCardEvent = 'create-job-card-for-vehicle';

// Helper function to format date
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

// Define custom meta type
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
  enableColumnFilter?: boolean;
};

export const columns: ExtendedColumnDef<VehicleListItem>[] = [
  {
    accessorKey: 'registration_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Registration" />,
    cell: ({ row }): JSX.Element => (
      <span className="font-medium">{row.getValue('registration_number')}</span>
    ),
    meta: {
      label: 'Registration',
      placeholder: 'Filter registration...',
      variant: 'text',
      icon: Car,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'make',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Make" />,
    cell: ({ row }): JSX.Element => <span>{row.getValue('make') || '-'}</span>,
    meta: {
      label: 'Make',
      placeholder: 'Filter make...',
      variant: 'text',
      icon: FileText,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'model',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Model" />,
    cell: ({ row }): JSX.Element => <span>{row.getValue('model') || '-'}</span>,
    meta: {
      label: 'Model',
      placeholder: 'Filter model...',
      variant: 'text',
      icon: FileText,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'year_of_manufacture',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Year" />,
    cell: ({ row }): JSX.Element => <span>{row.getValue('year_of_manufacture') || '-'}</span>,
    meta: {
      label: 'Year',
      placeholder: 'Filter year...',
      variant: 'number',
      icon: Calendar,
    },
    enableColumnFilter: false,
  },
  {
    accessorKey: 'color',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Color" />,
    cell: ({ row }): JSX.Element => <span>{row.getValue('color') || '-'}</span>,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'fuel_type',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Fuel" />,
    cell: ({ row }): JSX.Element => {
      const fuel = row.getValue('fuel_type') as string | null;
      return <span className="capitalize">{fuel || '-'}</span>;
    },
    meta: {
      label: 'Fuel Type',
      placeholder: 'Filter fuel type...',
      variant: 'select',
      icon: Fuel,
      options: [
        { label: 'Petrol', value: 'petrol' },
        { label: 'Diesel', value: 'diesel' },
        { label: 'CNG', value: 'cng' },
        { label: 'Electric', value: 'electric' },
        { label: 'Hybrid', value: 'hybrid' },
      ],
    },
    enableColumnFilter: true,
  },
  {
    id: 'owner.name',
    accessorKey: 'owner.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Owner" />,
    cell: ({ row }): JSX.Element => {
      const owner = row.original.owner;
      return (
        <div className="flex flex-col">
          <span>{owner?.name || '-'}</span>
          {owner?.phone && <span className="text-xs text-muted-foreground">{owner.phone}</span>}
        </div>
      );
    },
    meta: {
      label: 'Owner',
      placeholder: 'Filter owner...',
      variant: 'text',
      icon: User,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'last_service_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Service" />,
    cell: ({ row }): JSX.Element => {
      const date = row.getValue('last_service_date') as string | null;
      const odometer = row.original.last_service_odometer;
      return (
        <div className="flex flex-col">
          <span>{formatDate(date)}</span>
          {odometer && <span className="text-xs text-muted-foreground">{odometer} km</span>}
        </div>
      );
    },
    meta: {
      label: 'Last Service',
      placeholder: 'Select date range...',
      variant: 'dateRange',
      icon: Calendar,
    },
    enableColumnFilter: false,
  },
  {
    id: 'actions',
    cell: ({ row }): React.ReactNode => {
      const vehicle = row.original;

      const handleEdit = (): void => {
        const event = new CustomEvent(editVehicleEvent, {
          detail: { vehicle },
        });
        document.dispatchEvent(event);
      };

      const handleDelete = (): void => {
        const event = new CustomEvent(deleteVehicleEvent, {
          detail: { vehicle },
        });
        document.dispatchEvent(event);
      };

      const handleCreateJobCard = (): void => {
        const event = new CustomEvent(createJobCardEvent, {
          detail: { vehicle },
        });
        document.dispatchEvent(event);
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleCreateJobCard}>
              <FileText className="mr-2 h-4 w-4" />
              Create Job Card
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
              <Trash className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
